import { useQueryClient } from '@tanstack/react-query'
import { Popover, Select } from 'antd'
import { useAtom } from 'jotai'
import { useMemo, useState, type FC } from 'react'
import { FaUser } from 'react-icons/fa'

import { Button } from '@/components/elements'
import { LABELS } from '@/constants/common/message.constants'
import { type UserMeExEntity, type UserMeTenantExEntity } from '@/libs/api'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { AuthUserPopover } from '../AuthUserPopover'

import {
  buildDcPartnerDisplayName,
  buildDcPartnerFullDisplayName,
  buildPartnerSelectOptions
} from './build-partner-select-options'

type Props = {
  authUser: UserMeExEntity
  currentTenant: UserMeTenantExEntity
  onLogout: () => void
}

/**
 * パートナーユーザーの右上。拠点表示 (AuthUserPopoverTrigger) の代わりに、ログイン中の
 * パートナーの「パートナー名 / 事業所名」を出す (VOC_PJ-1107 / #6012)。
 * オーナーユーザーは従来どおり AuthUserPopoverTrigger なので、本コンポーネントには来ない。
 *
 * 右上で選ぶ対象は内部的には拠点 (selectedDcId / X-DC-Id) のままで、パートナーは拠点に 1:1 で
 * 紐づく。複数の拠点のパートナーに所属するユーザーだけ、hover で所属パートナーの切替プルダウンを出す。
 */
export const PartnerUserPopoverTrigger: FC<Props> = ({
  authUser,
  currentTenant,
  onLogout
}) => {
  const [selectedDcId, setSelectedDcId] = useAtom(selectedDcIdAtom)
  const { partners } = currentTenant

  // 切替単位は拠点 (dcId / X-DC-Id)。選択中拠点が所属拠点に無ければ先頭拠点を使う。
  const currentDcId = useMemo(
    () =>
      partners.some((x) => x.dcId === selectedDcId)
        ? selectedDcId
        : partners[0]?.dcId,
    [partners, selectedDcId]
  )

  const options = useMemo(() => buildPartnerSelectOptions(partners), [partners])

  // 複数の拠点に所属するユーザーだけ切替プルダウンを出す (同一拠点の複数パートナーは
  // 1 拠点扱い = 切替対象にならない)。
  const hasMultipleDcs = useMemo(
    () => new Set(partners.map((x) => x.dcId)).size > 1,
    [partners]
  )
  const [isOpenPartnerSelect, setIsOpenPartnerSelect] = useState(false)

  const queryClient = useQueryClient()
  // 選択中DC基準でフィルタされるマスタ(プルダウン等)が古いDCのキャッシュで残らないよう、
  // 拠点切替時にTanStack Queryのキャッシュを全クリアし、各画面のクエリを再フェッチさせる
  // (AuthUserPopoverTrigger の拠点切替と同じ扱い)
  const handleChangeDc = (value: number) => {
    if (value === selectedDcId) return
    setSelectedDcId(value)
    queryClient.clear()
  }

  const displayName = useMemo(
    () =>
      currentDcId == null
        ? ''
        : buildDcPartnerDisplayName(partners, currentDcId),
    [partners, currentDcId]
  )
  const fullDisplayName = useMemo(
    () =>
      currentDcId == null
        ? ''
        : buildDcPartnerFullDisplayName(partners, currentDcId),
    [partners, currentDcId]
  )

  // 長い名前や同一拠点の複数パートナーは省略し、全件は hover (title) で見える
  const label = (
    <div className="min-w-[100px] max-w-[360px] pb-2 leading-none text-secondary">
      <div className="pb-1 text-2xs">{LABELS.PARTNER_OFFICE}</div>
      <div className="truncate" title={fullDisplayName}>
        {displayName}
      </div>
    </div>
  )

  return (
    <div className="flex items-center gap-2">
      {hasMultipleDcs ? (
        <Popover
          trigger={['hover']}
          placement="bottomRight"
          arrow={false}
          open={isOpenPartnerSelect}
          onOpenChange={(newOpen) => setIsOpenPartnerSelect(newOpen)}
          content={
            <>
              <div className="pb-1 text-2xs text-secondary">
                {LABELS.PARTNER_OFFICE}
              </div>
              <Select
                value={currentDcId}
                showSearch
                optionFilterProp="label"
                options={options}
                className="w-[360px]"
                popupMatchSelectWidth={true}
                onChange={handleChangeDc}
                onSelect={() => setIsOpenPartnerSelect(false)}
              />
            </>
          }
        >
          {label}
        </Popover>
      ) : (
        label
      )}
      <Popover
        trigger={['hover']}
        placement="bottomRight"
        arrow={false}
        content={
          <AuthUserPopover
            authUser={authUser}
            currentTenant={currentTenant}
            onLogout={onLogout}
          />
        }
      >
        {/*
        Warning: findDOMNode is deprecated and will be removed in the next major release が発生するので Popoverの子要素にdivを挟む
        - https://github.com/ant-design/ant-design/issues/48709
      */}
        <div className="inline-block">
          <Button
            type="primary"
            className="flex h-[38px] w-[38px] items-center justify-center rounded-md p-0"
            data-testid="auth-user-popover-trigger"
          >
            <FaUser className="text-xs" />
          </Button>
        </div>
      </Popover>
    </div>
  )
}
